import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Appointment, TimeRange } from '@emr/shared';

import {
  bookMyAppointment,
  getProviderAvailability,
  listBookableProviders,
  listMyAppointments,
} from '../../api/patientPortal/scheduling.ts';
import { patientPortalErrorMessage } from '../../api/patientPortal/errors.ts';
import { AsyncSection } from '../../components/AsyncState.tsx';
import {
  Button,
  ErrorText,
  Field,
  Panel,
  SelectField,
  TextAreaField,
} from '../../components/primitives.tsx';
import { fromDatetimeLocalValue, toDatetimeLocalValue } from '../../schedule/datetimeLocalInput.ts';
import { statusToneClass } from '../../schedule/AppointmentList.tsx';
import { useDateTimeFormat } from '../../format/dateTimeFormatContext.ts';
import { formatDateAndTime } from '../../format/formatDateTime.ts';

const DEFAULT_WINDOW_DAYS = 14;
const DEFAULT_APPOINTMENT_MINUTES = 30;

function todayDateInputValue(): string {
  const now = new Date();
  return toDatetimeLocalValue(now.toISOString()).slice(0, 10);
}

function addDays(dateInputValue: string, days: number): string {
  const [year, month, day] = dateInputValue.split('-').map(Number);
  const date = new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1);
  date.setDate(date.getDate() + days);
  return toDatetimeLocalValue(date.toISOString()).slice(0, 10);
}

/**
 * Self-scheduling (`BACKLOG.md` W2 pillar 2, `docs/decisions/patient-portal-scope.md`'s
 * "Scheduling" section — direct self-service booking, no staff confirmation step, every provider
 * and visit type bookable with no curation).
 *
 * **`BACKLOG.md` W5**: a "your appointments" list at the top of the page, closing the gap this
 * file's own header used to flag — a patient revisiting this page previously had no way to see what
 * they already booked, since the only signal was the confirmation panel a booking action's own
 * response produces. `listMyAppointments` (`server/src/patientScheduling/routes.ts`'s new
 * `GET .../scheduling/appointments`) is refetched after a successful booking, the same way
 * `availabilityQuery` already is, so a freshly-booked appointment appears in the list immediately,
 * not only after a reload.
 */
export function PortalSchedulePage() {
  const { t } = useTranslation('portalSchedule');
  const { preference } = useDateTimeFormat();

  const [providerId, setProviderId] = useState('');
  const [fromDate, setFromDate] = useState(todayDateInputValue());
  const [toDate, setToDate] = useState(() => addDays(todayDateInputValue(), DEFAULT_WINDOW_DAYS));
  const [openRangeStart, setOpenRangeStart] = useState<string | null>(null);
  const [justBooked, setJustBooked] = useState<Appointment | null>(null);

  const myAppointmentsQuery = useQuery({
    queryKey: ['portal', 'scheduling', 'myAppointments'],
    queryFn: ({ signal }: { signal: AbortSignal }) => listMyAppointments(signal),
  });

  const providersQuery = useQuery({
    queryKey: ['portal', 'scheduling', 'providers'],
    queryFn: ({ signal }: { signal: AbortSignal }) => listBookableProviders(signal),
  });

  const from = fromDatetimeLocalValue(`${fromDate}T00:00`);
  const to = fromDatetimeLocalValue(`${toDate}T23:59`);

  const availabilityQuery = useQuery({
    queryKey: ['portal', 'scheduling', 'availability', providerId, from, to],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      getProviderAvailability({ providerId, from: from ?? '', to: to ?? '' }, signal),
    enabled: providerId !== '' && from !== null && to !== null,
  });

  return (
    <div className="flex flex-col gap-4">
      <h1 className="m-0 text-xl font-semibold text-ink">{t('heading')}</h1>

      <Panel title={t('myAppointments.panelTitle')}>
        <AsyncSection
          query={myAppointmentsQuery}
          loadingLabel={t('myAppointments.loading')}
          isEmpty={(data: readonly Appointment[]) => data.length === 0}
          emptyLabel={t('myAppointments.empty')}
        >
          {(myAppointments: readonly Appointment[]) => (
            <MyAppointmentsList appointments={myAppointments} />
          )}
        </AsyncSection>
      </Panel>

      <Panel title={t('search.panelTitle')}>
        <AsyncSection query={providersQuery} loadingLabel={t('search.loadingProviders')}>
          {(providers) => (
            <div className="flex flex-col gap-3">
              <SelectField
                label={t('search.providerLabel')}
                value={providerId}
                onChange={(event) => {
                  setProviderId(event.target.value);
                  setJustBooked(null);
                }}
              >
                <option value="">{t('search.providerPlaceholder')}</option>
                {providers.map((member) => (
                  <option key={member.userId} value={member.userId}>
                    {member.email} ({member.role})
                  </option>
                ))}
              </SelectField>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field
                  label={t('search.fromLabel')}
                  type="date"
                  value={fromDate}
                  onChange={(event) => {
                    setFromDate(event.target.value);
                  }}
                />
                <Field
                  label={t('search.toLabel')}
                  type="date"
                  value={toDate}
                  onChange={(event) => {
                    setToDate(event.target.value);
                  }}
                />
              </div>
            </div>
          )}
        </AsyncSection>
      </Panel>

      {justBooked !== null ? (
        <Panel title={t('confirmation.panelTitle')}>
          <p className="m-0 text-sm text-ink">
            {t('confirmation.body', {
              type: justBooked.appointmentType,
              time: formatDateAndTime(justBooked.startsAt, preference),
            })}
          </p>
        </Panel>
      ) : null}

      {providerId === '' ? (
        <p className="m-0 text-sm text-ink-muted">{t('search.selectProviderPrompt')}</p>
      ) : (
        <AsyncSection<readonly TimeRange[]>
          query={availabilityQuery}
          loadingLabel={t('availability.loading')}
          isEmpty={(data) => data.length === 0}
          emptyLabel={t('availability.empty')}
        >
          {(ranges) => (
            <div className="flex flex-col gap-2">
              {ranges.map((range) => (
                <AvailableRangeCard
                  key={range.startsAt}
                  range={range}
                  providerId={providerId}
                  isOpen={openRangeStart === range.startsAt}
                  onToggle={() => {
                    setOpenRangeStart((current) =>
                      current === range.startsAt ? null : range.startsAt,
                    );
                  }}
                  onBooked={(appointment) => {
                    setJustBooked(appointment);
                    setOpenRangeStart(null);
                    void availabilityQuery.refetch();
                    void myAppointmentsQuery.refetch();
                  }}
                />
              ))}
            </div>
          )}
        </AsyncSection>
      )}
    </div>
  );
}

/**
 * `BACKLOG.md` W5's list, split into upcoming vs. past by comparing `startsAt` against the current
 * time client-side — the server route itself returns everything, oldest-first, with no `from`/`to`
 * filter (`patientScheduling/routes.ts`'s own header on why: a booking-history read, not a
 * calendar-range one), so the split is purely a presentation decision made here rather than a second
 * server round trip. Cancelled and completed appointments are included in whichever bucket their own
 * `startsAt` falls into — a cancelled appointment for a future date still reads as "upcoming" in the
 * sense of belonging with the rest of what's ahead, with its status label telling the real story.
 *
 * **Status color reuses `AppointmentList.tsx`'s `statusToneClass` (found by code review to be a
 * missed reuse in an earlier pass), but deliberately not that same file's `STATUS_LABELS`.**
 * `statusToneClass` is a pure status → Tailwind-class mapping with no English text in it, so sharing
 * it is a clean win — one status→colour decision, not two that could drift. `STATUS_LABELS` is a
 * hardcoded-English `Record`, unsuitable for this bilingual portal; the label text below stays driven
 * by this file's own `myAppointments.status.*` i18n keys instead, which is the correct place for that
 * text to live even though it means the status vocabulary itself (which five states exist) is still
 * declared twice — once in `@emr/shared`'s `APPOINTMENT_STATUSES`, once implicitly by this file's own
 * `en`/`portalSchedule.json` key set, the same duplication `statusActions.ts` itself already has
 * relative to `APPOINTMENT_STATUSES`.
 */
function MyAppointmentsList({ appointments }: { readonly appointments: readonly Appointment[] }) {
  const { t } = useTranslation('portalSchedule');
  const { preference } = useDateTimeFormat();

  const now = new Date().getTime();
  const upcoming = appointments.filter((a) => new Date(a.startsAt).getTime() >= now);
  const past = appointments.filter((a) => new Date(a.startsAt).getTime() < now).toReversed(); // most-recent-past-first, the direction a patient actually wants to scan

  return (
    <div className="flex flex-col gap-4">
      <MyAppointmentsGroup
        heading={t('myAppointments.upcomingHeading')}
        emptyLabel={t('myAppointments.noUpcoming')}
        appointments={upcoming}
        preference={preference}
      />
      <MyAppointmentsGroup
        heading={t('myAppointments.pastHeading')}
        emptyLabel={t('myAppointments.noPast')}
        appointments={past}
        preference={preference}
      />
    </div>
  );
}

function MyAppointmentsGroup({
  heading,
  emptyLabel,
  appointments,
  preference,
}: {
  readonly heading: string;
  readonly emptyLabel: string;
  readonly appointments: readonly Appointment[];
  readonly preference: Parameters<typeof formatDateAndTime>[1];
}) {
  const { t } = useTranslation('portalSchedule');

  return (
    <div className="flex flex-col gap-2">
      <h2 className="m-0 text-sm font-semibold text-ink">{heading}</h2>
      {appointments.length === 0 ? (
        <p className="m-0 text-sm text-ink-muted">{emptyLabel}</p>
      ) : (
        <ul className="m-0 flex flex-col gap-2 p-0">
          {appointments.map((appointment) => (
            <li
              key={appointment.id}
              className="flex flex-col gap-1 rounded-control border border-line bg-surface p-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="flex flex-col">
                <span className="text-sm font-medium text-ink">{appointment.appointmentType}</span>
                <span className="text-sm text-ink-muted">
                  {formatDateAndTime(appointment.startsAt, preference)}
                </span>
              </div>
              <span className={`text-sm font-medium ${statusToneClass(appointment.status)}`}>
                {t(`myAppointments.status.${appointment.status}`)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AvailableRangeCard({
  range,
  providerId,
  isOpen,
  onToggle,
  onBooked,
}: {
  readonly range: TimeRange;
  readonly providerId: string;
  readonly isOpen: boolean;
  readonly onToggle: () => void;
  readonly onBooked: (appointment: Appointment) => void;
}) {
  const { t } = useTranslation('portalSchedule');
  const { preference } = useDateTimeFormat();

  const defaultEnd = new Date(
    Math.min(
      new Date(range.startsAt).getTime() + DEFAULT_APPOINTMENT_MINUTES * 60_000,
      new Date(range.endsAt).getTime(),
    ),
  ).toISOString();

  const [appointmentType, setAppointmentType] = useState('');
  const [startsAt, setStartsAt] = useState(toDatetimeLocalValue(range.startsAt));
  const [endsAt, setEndsAt] = useState(toDatetimeLocalValue(defaultEnd));
  const [notes, setNotes] = useState('');

  const book = useMutation({
    mutationFn: bookMyAppointment,
    onSuccess: (appointment) => {
      onBooked(appointment);
    },
  });

  return (
    <div className="rounded-control border border-line bg-surface p-3">
      <div className="flex items-center justify-between gap-3">
        <p className="m-0 text-sm text-ink">
          {formatDateAndTime(range.startsAt, preference)} –{' '}
          {formatDateAndTime(range.endsAt, preference)}
        </p>
        <Button variant="secondary" onClick={onToggle}>
          {isOpen ? t('availability.cancel') : t('availability.book')}
        </Button>
      </div>

      {isOpen ? (
        <form
          className="mt-3 flex flex-col gap-3 border-t border-line pt-3"
          onSubmit={(event) => {
            event.preventDefault();
            const startIso = fromDatetimeLocalValue(startsAt);
            const endIso = fromDatetimeLocalValue(endsAt);
            if (startIso === null || endIso === null) return;
            book.mutate({
              providerId,
              appointmentType,
              startsAt: startIso,
              endsAt: endIso,
              ...(notes.trim() === '' ? {} : { notes }),
            });
          }}
        >
          <Field
            label={t('availability.typeLabel')}
            required
            value={appointmentType}
            onChange={(event) => {
              setAppointmentType(event.target.value);
            }}
          />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field
              label={t('availability.startLabel')}
              type="datetime-local"
              required
              value={startsAt}
              onChange={(event) => {
                setStartsAt(event.target.value);
              }}
            />
            <Field
              label={t('availability.endLabel')}
              type="datetime-local"
              required
              value={endsAt}
              onChange={(event) => {
                setEndsAt(event.target.value);
              }}
            />
          </div>
          <TextAreaField
            label={t('availability.notesLabel')}
            rows={2}
            value={notes}
            onChange={(event) => {
              setNotes(event.target.value);
            }}
          />

          {book.isError ? <ErrorText message={patientPortalErrorMessage(book.error)} /> : null}

          <Button type="submit" pending={book.isPending} className="self-start">
            {book.isPending ? t('availability.submitting') : t('availability.confirm')}
          </Button>
        </form>
      ) : null}
    </div>
  );
}
